import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Download, TrendingUp, AlertCircle, RefreshCw } from "lucide-react";

const SENT_CLS = {
  positive: "bg-emerald-100 text-emerald-700",
  neutral: "bg-zinc-100 text-zinc-600",
  negative: "bg-rose-100 text-rose-700",
  uncertain: "bg-amber-100 text-amber-700",
};

const badge = (s) => `text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${SENT_CLS[s] || "bg-zinc-100 text-zinc-600"}`;

export default function CoachCorrectionsReport() {
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from_, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [channel, setChannel] = useState("");
  const [origSent, setOrigSent] = useState("");
  const [corrSent, setCorrSent] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (from_) p.set("from_", from_);
      if (to) p.set("to", to);
      if (channel) p.set("channel", channel);
      if (origSent) p.set("original_sentiment", origSent);
      if (corrSent) p.set("corrected_sentiment", corrSent);
      const { data } = await api.get(`/business-coach/notifications/corrections/report?${p.toString()}`);
      setReport(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportCsv = async () => {
    const p = new URLSearchParams();
    if (from_) p.set("from_", from_);
    if (to) p.set("to", to);
    if (channel) p.set("channel", channel);
    if (origSent) p.set("original_sentiment", origSent);
    if (corrSent) p.set("corrected_sentiment", corrSent);
    try {
      const resp = await api.get(`/business-coach/notifications/corrections/export.csv?${p.toString()}`,
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `leadforge-coach-corrections-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(`${API}/business-coach/notifications/corrections/export.csv?${p.toString()}`, "_blank");
    }
  };

  const totals = report?.totals || {};
  const recent = report?.recent || [];
  const pairs = report?.top_confused_pairs || [];
  const trend = report?.trend_by_day || {};
  const recs = report?.recommendations || [];

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="coach-corrections-report">
      <button
        onClick={() => nav("/app/coach")}
        data-testid="corrections-back"
        className="flex items-center gap-1 text-sm text-zinc-500 hover:text-[#FF5A00] mb-3"
      >
        <ArrowLeft className="w-4 h-4"/> Back to Coach
      </button>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">
            Classification Corrections
          </div>
          <h1 className="font-heading text-3xl font-bold">Reply classifier accuracy</h1>
          <p className="text-sm text-zinc-500 mt-1">Every time you overrode the AI-assigned sentiment or intent. Used to spot patterns and tune the rules.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="corrections-refresh">
          <RefreshCw className="w-3 h-3 mr-1"/> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="All replies" value={totals.all_replies ?? 0} testid="corrections-kpi-all"/>
        <Kpi label="Corrected" value={totals.corrected ?? 0} testid="corrections-kpi-corrected"/>
        <Kpi
          label="Accuracy rate"
          value={totals.accuracy_rate_percent == null ? "—" : `${totals.accuracy_rate_percent}%`}
          testid="corrections-kpi-accuracy"
        />
        <Kpi
          label="Trend days"
          value={Object.keys(trend).length}
          testid="corrections-kpi-trend"
        />
      </div>

      <Card className="p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">From</label>
            <Input type="date" value={from_} onChange={(e) => setFrom(e.target.value)}
                   data-testid="corrections-from" className="text-xs"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">To</label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                   data-testid="corrections-to" className="text-xs"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Channel</label>
            <select value={channel} onChange={(e) => setChannel(e.target.value)}
                    data-testid="corrections-channel"
                    className="border border-zinc-200 rounded-md px-2 py-1.5 text-xs w-full">
              <option value="">All</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Original sentiment</label>
            <select value={origSent} onChange={(e) => setOrigSent(e.target.value)}
                    data-testid="corrections-orig-sent"
                    className="border border-zinc-200 rounded-md px-2 py-1.5 text-xs w-full">
              <option value="">All</option>
              <option value="positive">Positive</option>
              <option value="neutral">Neutral</option>
              <option value="negative">Negative</option>
              <option value="uncertain">Uncertain</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Corrected sentiment</label>
            <select value={corrSent} onChange={(e) => setCorrSent(e.target.value)}
                    data-testid="corrections-corr-sent"
                    className="border border-zinc-200 rounded-md px-2 py-1.5 text-xs w-full">
              <option value="">All</option>
              <option value="positive">Positive</option>
              <option value="neutral">Neutral</option>
              <option value="negative">Negative</option>
              <option value="uncertain">Uncertain</option>
            </select>
          </div>
          <div className="flex gap-2">
            <Button onClick={load} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white flex-1"
                    data-testid="corrections-apply">Apply</Button>
            <Button variant="outline" onClick={exportCsv} data-testid="corrections-export">
              <Download className="w-3 h-3"/>
            </Button>
          </div>
        </div>
      </Card>

      {recs.length > 0 && (
        <Card className="p-4 mb-6 bg-amber-50 border-amber-200" data-testid="corrections-recommendations">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-amber-700"/>
            <div className="text-xs font-mono-accent uppercase tracking-widest text-amber-800">Recommendations</div>
          </div>
          <ul className="text-sm text-zinc-800 space-y-1">
            {recs.map((r, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-amber-700">•</span>
                <span>{r.suggestion}</span>
              </li>
            ))}
          </ul>
          <div className="text-[10px] text-zinc-500 mt-2 italic">
            Not auto-applied. Review before adjusting classifier rules.
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-2 flex items-center gap-2">
            <TrendingUp className="w-3 h-3"/> Top confusion pairs
          </div>
          {pairs.length === 0 ? (
            <div className="text-sm text-zinc-400" data-testid="corrections-pairs-empty">No corrections yet.</div>
          ) : (
            <ul className="space-y-2">
              {pairs.map((p, i) => (
                <li key={i} className="flex items-center justify-between text-sm" data-testid={`corrections-pair-${i}`}>
                  <span className="font-mono text-xs">{p.pair}</span>
                  <span className="text-[#FF5A00] font-semibold">{p.count}×</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="p-4">
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-2">By channel</div>
          <div className="flex gap-4 text-sm">
            <div><span className="text-zinc-500">Email:</span> <b>{report?.by_channel?.email ?? 0}</b></div>
            <div><span className="text-zinc-500">SMS:</span> <b>{report?.by_channel?.sms ?? 0}</b></div>
          </div>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Original sentiment breakdown</div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(report?.by_original_sentiment || {}).map(([k, v]) => (
                <span key={k} className={badge(k)}>{k} · {v}</span>
              ))}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 mt-3">Corrected sentiment breakdown</div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(report?.by_corrected_sentiment || {}).map(([k, v]) => (
                <span key={k} className={badge(k)}>{k} · {v}</span>
              ))}
            </div>
          </div>
        </Card>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="corrections-table">
          <thead className="bg-zinc-50 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-4 py-2">Corrected at</th>
              <th className="text-left px-4 py-2">Channel</th>
              <th className="text-left px-4 py-2">Sentiment</th>
              <th className="text-left px-4 py-2">Intent</th>
              <th className="text-left px-4 py-2">Reason</th>
              <th className="text-left px-4 py-2">Body</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="text-center py-6 text-zinc-500">Loading…</td></tr>
            )}
            {!loading && recent.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-zinc-500" data-testid="corrections-empty">
                No corrections in this window.
              </td></tr>
            )}
            {!loading && recent.map((r) => (
              <tr key={r.id} className="border-b border-zinc-100 hover:bg-orange-50/30"
                  data-testid={`corrections-row-${r.id}`}>
                <td className="px-4 py-3 text-xs text-zinc-600">
                  {(r.corrected_at || "").replace("T", " ").slice(0, 16)}
                </td>
                <td className="px-4 py-3 text-xs uppercase">{r.channel}</td>
                <td className="px-4 py-3 text-xs">
                  <span className={badge(r.original_sentiment)}>{r.original_sentiment || "—"}</span>
                  <span className="mx-1 text-zinc-400">→</span>
                  <span className={badge(r.sentiment)}>{r.sentiment || "—"}</span>
                </td>
                <td className="px-4 py-3 text-xs">
                  {r.original_intent && <span className="text-zinc-500">{(r.original_intent || "").replace(/_/g, " ")}</span>}
                  {r.original_intent && <span className="mx-1 text-zinc-400">→</span>}
                  <span>{(r.intent || "—").replace(/_/g, " ")}</span>
                </td>
                <td className="px-4 py-3 text-xs text-zinc-700 max-w-xs">
                  {r.correction_reason || <span className="text-zinc-400">—</span>}
                </td>
                <td className="px-4 py-3 text-xs text-zinc-500 max-w-md truncate">{r.body_excerpt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono-accent">{label}</div>
      <div className="font-heading text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
